import React, { useEffect, useState } from "react";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import { Bot, ChevronLeft, ChevronRight, GraduationCap, Lightbulb, TriangleAlert, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { t, tRich, useLang } from "../../lib/i18n";
import {
  CombosVisual,
  GoalVisual,
  JokerVisual,
  PoseVisual,
  ReadyVisual,
  SaveVisual,
  ScoreVisual,
  TableVisual,
  TurnVisual,
} from "./TutorialVisuals";

/**
 * "Tutoriel" — how to play, for new players: opened by itself at the start of
 * their first game, then from the Lobby header. One idea per step: a picture,
 * a title, two lines and at most one tip or warning. Back / Next, the progress
 * bar, a swipe or the arrow keys move between steps; from the Lobby, the last
 * one offers a practice game against a beginner bot.
 * The rules quoted are the default ones (threshold 71, penalties of 50): keep
 * the texts (`tuto.*` in lib/i18n) in step with backend/game.
 */
const STEPS = [
  { id: "goal", Visual: GoalVisual, title: "tuto.goal.title", body: "tuto.goal.body", tip: "tuto.goal.tip" },
  { id: "turn", Visual: TurnVisual, title: "tuto.turn.title", body: "tuto.turn.body", tip: "tuto.turn.tip" },
  { id: "combos", Visual: CombosVisual, title: "tuto.combos.title", body: "tuto.combos.body", tip: "tuto.combos.tip" },
  { id: "joker", Visual: JokerVisual, title: "tuto.joker.title", body: "tuto.joker.body", tip: "tuto.joker.tip" },
  { id: "save", Visual: SaveVisual, title: "tuto.save.title", body: "tuto.save.body", tip: "tuto.save.tip" },
  { id: "nemna3", Visual: PoseVisual, title: "tuto.nemna3.title", body: "tuto.nemna3.body", tip: "tuto.nemna3.tip" },
  { id: "table", Visual: TableVisual, title: "tuto.table.title", body: "tuto.table.body", warn: "tuto.table.warn" },
  { id: "score", Visual: ScoreVisual, title: "tuto.score.title", body: "tuto.score.body", tip: "tuto.score.tip" },
  // In a game there is no practice game to offer: the last words point to the Dealer.
  { id: "ready", Visual: ReadyVisual, title: "tuto.ready.title", body: "tuto.ready.body", bodyInGame: "tuto.ready.bodyGame" },
];
const LAST = STEPS.length - 1;

// A step slides in from the side it comes from; in Tunisian the next step
// comes from the left.
const SLIDE = {
  enter: ({ dir, sign }) => ({ x: dir * sign * 40, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: ({ dir, sign }) => ({ x: -dir * sign * 40, opacity: 0 }),
};

// The words of a step, under its picture.
function StepText({ step, inGame, onPractice, practiceDisabled }) {
  return (
    <>
      <h3 className="font-display text-xl leading-snug text-amber-100">{t(step.title)}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-slate-300 [&_b]:font-semibold [&_b]:text-white">
        {tRich((inGame && step.bodyInGame) || step.body)}
      </p>
      {step.tip && <Note text={t(step.tip)} />}
      {step.warn && <Note warn text={t(step.warn)} />}
      {step.id === "ready" && onPractice && (
        <div className="mt-4">
          <button
            type="button"
            onClick={onPractice}
            disabled={practiceDisabled}
            data-testid="tutorial-practice-btn"
            className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 text-sm font-semibold text-white shadow-lg shadow-emerald-900/40 transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Bot className="h-4 w-4" />
            {t("tuto.ready.practice")}
          </button>
          {practiceDisabled && <p className="mt-1.5 text-center text-xs text-slate-500">{t("lobby.quitFirst")}</p>}
        </div>
      )}
    </>
  );
}

function Note({ warn = false, text }) {
  const Icon = warn ? TriangleAlert : Lightbulb;
  return (
    <p
      className={`mt-3 flex gap-2 rounded-lg border px-3 py-2 text-xs leading-relaxed ${
        warn ? "border-rose-500/20 bg-rose-500/[0.07] text-rose-100/90" : "border-amber-500/15 bg-amber-500/[0.07] text-amber-100/90"
      }`}
    >
      <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${warn ? "text-rose-300" : "text-amber-300"}`} />
      <span>{text}</span>
    </p>
  );
}

// Lives inside the dialog, so every opening starts again from the first step.
function Steps({ onClose, inGame, onPractice, practiceDisabled }) {
  const { isRtl } = useLang();
  const [[index, dir], setPage] = useState([0, 0]);
  const sign = isRtl ? -1 : 1;
  const step = STEPS[index];
  const { Visual } = step;
  const finale = index === LAST && onPractice;

  const go = (to) => {
    const next = Math.max(0, Math.min(LAST, to));
    if (next !== index) setPage([next, next > index ? 1 : -1]);
  };

  // Arrow keys follow the screen: → is "next" in French, "back" in Tunisian.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      e.preventDefault();
      go(index + ((e.key === "ArrowRight") !== isRtl ? 1 : -1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Swiping the card towards the start of the line brings the next step.
  const onDragEnd = (_, { offset, velocity }) => {
    const swipe = -(offset.x + velocity.x * 0.2) * sign;
    if (swipe > 60) go(index + 1);
    else if (swipe < -60) go(index - 1);
  };

  return (
    <>
      <div className="flex items-center gap-2 px-4 pt-4 sm:px-5">
        <GraduationCap className="h-4 w-4 shrink-0 text-amber-300" />
        <DialogTitle className="text-sm font-semibold leading-none text-amber-200">{t("tuto.title")}</DialogTitle>
        <span className="ms-auto text-xs tabular-nums text-slate-500">
          <span dir="ltr">{index + 1}/{STEPS.length}</span>
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("tuto.close")}
          data-testid="tutorial-close"
          className="-me-1.5 rounded-full p-1.5 text-slate-500 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress: one segment per step, tap one to jump to it */}
      <div className="flex gap-1 px-4 pt-2 sm:px-5">
        {STEPS.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => go(i)}
            aria-label={t(s.title)}
            aria-current={i === index ? "step" : undefined}
            className="flex-1 py-1.5"
          >
            <span
              className={`block h-1 rounded-full transition-colors duration-300 ${
                i === index ? "bg-amber-400" : i < index ? "bg-amber-400/45" : "bg-white/10"
              }`}
            />
          </button>
        ))}
      </div>

      <div aria-live="polite" className="overflow-hidden">
        <AnimatePresence mode="wait" initial={false} custom={{ dir, sign }}>
          <motion.div
            key={step.id}
            custom={{ dir, sign }}
            variants={SLIDE}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.2, ease: "easeOut" }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.15}
            onDragEnd={onDragEnd}
            className="select-none px-4 sm:px-5"
            data-testid={`tutorial-step-${step.id}`}
          >
            {/* The picture is decoration: the text says it all */}
            <div
              aria-hidden="true"
              inert
              className="rami-mat pointer-events-none mt-1.5 flex h-[208px] items-center justify-center overflow-hidden rounded-xl border border-emerald-400/10"
            >
              <Visual />
            </div>
            {/* The words of every step share one cell; the hidden ones only hold
                the height of the longest, so the popup keeps one size and the
                buttons stay where the finger is, from step to step. */}
            <div className="grid pt-4">
              {STEPS.map((s) => (
                <div
                  key={s.id}
                  aria-hidden={s.id === step.id ? undefined : true}
                  className={`col-start-1 row-start-1 ${s.id === step.id ? "" : "invisible"}`}
                >
                  <StepText step={s} inGame={inGame} onPractice={onPractice} practiceDisabled={practiceDisabled} />
                </div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-3 flex items-center gap-2 border-t border-white/5 px-4 py-3 sm:px-5">
        <button
          type="button"
          onClick={index === 0 ? onClose : () => go(index - 1)}
          data-testid="tutorial-back"
          className="inline-flex h-10 items-center gap-1 rounded-full px-3 text-sm font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
        >
          {index > 0 && <ChevronLeft className="h-4 w-4 rtl:rotate-180" />}
          {index === 0 ? t("tuto.skip") : t("common.back")}
        </button>
        {/* One button for "next" and "done", so the focus stays on it */}
        <button
          type="button"
          autoFocus
          onClick={index < LAST ? () => go(index + 1) : onClose}
          data-testid="tutorial-next"
          className={`ms-auto inline-flex h-10 items-center gap-1 rounded-full ps-5 pe-4 text-sm font-bold transition-colors ${
            finale
              ? "bg-white/10 text-white hover:bg-white/15"
              : "bg-amber-500 text-slate-950 shadow-lg shadow-amber-500/20 hover:bg-amber-400 active:bg-amber-300"
          }`}
        >
          {index < LAST ? t("tuto.next") : t("tuto.done")}
          {index < LAST && <ChevronRight className="h-4 w-4 rtl:rotate-180" />}
        </button>
      </div>
    </>
  );
}

export function TutorialDialog({ open, onOpenChange, inGame = false, onPractice, practiceDisabled = false }) {
  const close = () => onOpenChange(false);
  const practice = onPractice && (() => { close(); onPractice(); });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        data-testid="tutorial-dialog"
        className="max-h-[94dvh] w-[calc(100vw-1.5rem)] max-w-md gap-0 overflow-y-auto overflow-x-hidden rounded-2xl border-amber-500/20 bg-slate-900 p-0 text-white shadow-2xl shadow-black/60 sm:rounded-2xl [&>button]:hidden"
      >
        <MotionConfig reducedMotion="user">
          <Steps onClose={close} inGame={inGame} onPractice={practice} practiceDisabled={practiceDisabled} />
        </MotionConfig>
      </DialogContent>
    </Dialog>
  );
}

const SEEN_KEY = "rami_tuto_seen";

function readSeen() {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return true;
  }
}

function markSeen() {
  try { localStorage.setItem(SEEN_KEY, "1"); } catch {}
}

/** Whether the tutorial was seen once (the gold dot of its entry in the Lobby), and a way to mark it. */
export function useTutorialSeen() {
  const [seen, setSeen] = useState(readSeen);
  const mark = () => {
    setSeen(true);
    markSeen();
  };
  return [seen, mark];
}

/**
 * Opens the tutorial by itself once: at the start of the player's first game
 * after installing (nothing in localStorage yet), a moment after the board
 * shows (`ready`). After that it only opens from the Lobby (« Apprendre à jouer », the settings).
 */
export function FirstGameTutorial({ ready }) {
  useLang();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!ready || readSeen()) return undefined;
    const timer = setTimeout(() => {
      markSeen();
      setOpen(true);
    }, 800);
    return () => clearTimeout(timer);
  }, [ready]);

  return <TutorialDialog open={open} onOpenChange={setOpen} inGame />;
}

export default TutorialDialog;
